import { Router } from '@angular/router';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { encodedDepartmentsToSelection } from 'src/app/common/departmentFilterUtils';
import { AutocompleteSearchResultsModel } from 'src/app/models/api/autocompleteSearchResults.model';
import { ResourceTypesPerPlatform } from 'src/app/models/api/common';
import { deduplicateResourceTypesForUI } from 'src/app/common/vertical-filters-utils';
import { InventoryResourceModel, ResourceCategory } from 'src/app/models/api/inventoryResource.model';
import { BusinessCriticalitiesSelectOptions } from 'src/app/models/selectOptions/businessCriticalitiesSelectOptions.model';
import { ConnectionConnectorTypeSelectOptions } from 'src/app/models/selectOptions/connectionConnectorTypeSelectOptions.model';
import { CreationTimeSinceOptions } from 'src/app/models/selectOptions/creationTimeSinceOptions';
import { LastModifiedSinceOptions } from 'src/app/models/selectOptions/lastModifiedSinceOptions.model';
import { RefreshMethodSelectOptions } from 'src/app/models/selectOptions/refreshMethodSelectOptions.model';
import { ResourceStatusSelectOptions } from 'src/app/models/selectOptions/resourceStatusSelectOptions.model';
import { SecurityRiskSelectOptions } from 'src/app/models/selectOptions/securityRisksSelectOptions.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { InventoryTableColumns } from 'src/app/models/tableColumns/inventoryTableColumn.model';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { InventoryHttpService } from 'src/app/services/httpServices/inventoryHttpService/inventory-http.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { IntegrationModel } from '../../../api';
import { ISelectOption, PlatformSelectOptions } from '../../../selectOptions';
import { getFilteredResourceCategorySelectOptions } from '../../../selectOptions/resourceCategorySelectOptions.model';
import { ResourceTypeSelectOptions } from '../../../selectOptions/resourceTypeSelectOptions.model';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { BooleanFilter } from '../filtersTypes/booleanFilter.model';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { TextFilter } from '../filtersTypes/textFilter.model';
import {
  checkAllGivenIdsExistInOptions,
  fromIntegrationToSelectOption,
  removeUnusedFilterByFeatureFlag,
} from './utils';
import {
  AiServiceSelectOptions,
  AiServiceSelectOptionsWithRsaDemo,
} from 'src/app/models/selectOptions/aiServiceSelectOptions.mode';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';

export class InventoryFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public identityIsExternal: BooleanFilter;
  public allBuilders: BooleanFilter;
  public connectionInUse: BooleanFilter;
  public automationUsesAiModel: BooleanFilter;
  public automationUsesWebhook: BooleanFilter;
  public automationUsesHttpStepsWithoutAuthentication: BooleanFilter;
  public automationIsBotTrigger: BooleanFilter;
  public isMcpHost: BooleanFilter;
  public environmentType: TextFilter;
  public botType: TextFilter;
  public search: TextFilter;
  public relatedResource: TextFilter;
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public lastModifiedSinceDate: MultiSelectFilters;
  public creationTimeSinceDate: MultiSelectFilters;
  public connectionConnectorType: MultiSelectFilters;
  public resourceCategoryFilters: MultiSelectFilters;
  public typeFilters: MultiSelectFilters;
  public resourceStatusFilters: MultiSelectFilters;
  public platformFilters: MultiSelectFilters;
  public businessCriticalityFilters: MultiSelectFilters;
  public securityRiskFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public refreshMethodFilters: MultiSelectFilters;
  public environmentFilters: DynamicMultiSelectFilters;
  public createdByFilters: DynamicMultiSelectFilters;
  public ownedByFilters: DynamicMultiSelectFilters;
  public departmentFilters: DynamicMultiSelectFilters;
  public featureFlagTypes = FeatureFlagTypes;
  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    undefined
  );
  public orgWideAccess: BooleanFilter;
  public publicAccess: BooleanFilter;
  public hasWritePermission: BooleanFilter;
  private _defaultSortColumn: SortColumn | undefined = undefined;
  // Persist baseline AI Service options (from an unselected state) so that when a user selects
  // a single service we can still display the broader set until fresh facets arrive.
  private _aiServiceBaselineOptions: ISelectOption[] | undefined;

  private readonly _identityIsExternalOptions = {
    id: 'isExternal',
    value: 'isExternal',
    displayName: 'Identity is External',
    filterType: FilterTypesEnum.IdentityIsExternal,
  } as ISelectOption;

  private readonly _allBuildersOptions = {
    id: 'allBuilders',
    value: 'allBuilders',
    displayName: 'All Builders',
    filterType: FilterTypesEnum.allBuilders,
  } as ISelectOption;

  private readonly _connectionNotInUseOptions = {
    id: 'connectionNotInUse',
    value: 'connectionNotInUse',
    displayName: 'Connection not in Use',
    filterType: FilterTypesEnum.ConnectionInUse,
  } as ISelectOption;

  private readonly _automationUsesAiModelOptions = {
    id: 'automationUsesAiModel',
    value: 'automationUsesAiModel',
    displayName: 'Automation uses an AI model',
    filterType: FilterTypesEnum.AutomationUsesAiModel,
  } as ISelectOption;

  private readonly _automationUsesWebhook = {
    id: 'automationUsesWebhook',
    value: 'automationUsesWebhook',
    displayName: 'Automation uses a webhook',
    filterType: FilterTypesEnum.AutomationUsesWebhook,
  } as ISelectOption;

  private readonly _automationUsesHttpStepsWithoutAuthentication = {
    id: 'automationUsesHttpStepsWithoutAuthentication',
    value: 'automationUsesHttpStepsWithoutAuthentication',
    displayName: 'Automation uses http step without authentication',
    filterType: FilterTypesEnum.AutomationUsesHttpStepsWithoutAuthentication,
  } as ISelectOption;

  private readonly _automationIsBotTrigger = {
    id: 'automationIsBotTrigger',
    value: 'automationIsBotTrigger',
    displayName: 'Automation is an agent trigger',
    filterType: FilterTypesEnum.AutomationIsBotTrigger,
  } as ISelectOption;

  private readonly _orgWideAccessOptions = {
    id: 'orgWideAccess',
    value: 'orgWideAccess',
    displayName: 'Org Wide Access',
    filterType: FilterTypesEnum.OrgWideAccess,
  } as ISelectOption;

  private readonly _publicAccessOptions = {
    id: 'publicAccess',
    value: 'publicAccess',
    displayName: 'Public Access',
    filterType: FilterTypesEnum.PublicAccess,
  } as ISelectOption;

  private readonly _isMcpHostOptions = {
    id: 'isMcpHost',
    value: 'isMcpHost',
    displayName: 'MCP Host',
    filterType: FilterTypesEnum.IsMcpHost,
  } as ISelectOption;

  private readonly _hasWritePermissionOptions = {
    id: 'hasWritePermission',
    value: 'hasWritePermission',
    displayName: 'Has Write Permission',
    filterType: FilterTypesEnum.HasWritePermission,
  } as ISelectOption;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _inventoryHttpService: InventoryHttpService,
    public featureFlagsService: FeatureFlagsService,
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _permissionsService: PermissionsService
  ) {
    this._initializeFilters();

    this._integrationsOnUpdate();
    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());

    this._initDefaultSortColumn();
  }

  // Facets
  public applyAiServiceFacetsIfProvided(facets?: { [facetName: string]: { [bucket: string]: number } }) {
    // Backend returns camelCase key: "aiService"
    const facetKey = 'aiService';
    if (!facets || !facets[facetKey]) {
      return;
    }
    const aiCounts = facets[facetKey];
    // Always start from the full catalog so previously filtered-out options can reappear
    const isRsaDemo = this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
    const allOptions = isRsaDemo ? AiServiceSelectOptionsWithRsaDemo : AiServiceSelectOptions;
    const hasAny = Object.keys(aiCounts).length > 0;
    const selectedIds = new Set(this.aiServiceFilters.getSelectedFiltersIds());
    // Show only options with count > 0 when facets are provided; if none, show none
    let filtered = hasAny ? allOptions.filter((opt) => (aiCounts[opt.value] || 0) > 0) : [];

    // If nothing is selected, capture a baseline set for future toggles
    if (selectedIds.size === 0) {
      this._aiServiceBaselineOptions = filtered.length > 0 ? filtered : undefined;
    } else if (this._aiServiceBaselineOptions && this._aiServiceBaselineOptions.length > 0) {
      // Union the filtered set with baseline so options don't collapse to a single choice
      const merged = new Map<string, ISelectOption>();
      [...filtered, ...this._aiServiceBaselineOptions].forEach((opt) => merged.set(opt.id, opt));
      filtered = Array.from(merged.values());
    }
    // Always keep currently selected options visible even if their count is 0/missing
    const selectedOptionsToKeep = allOptions.filter((opt) => selectedIds.has(opt.id));
    const mergedById = new Map<string, (typeof allOptions)[number]>();
    [...filtered, ...selectedOptionsToKeep].forEach((opt) => mergedById.set(opt.id, opt));
    filtered = Array.from(mergedById.values());
    const shouldUpdate = this.aiServiceFilters.updateOptions(filtered);
    this._updateAllOptions();
    if (shouldUpdate) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  // Fallback when server facets are missing: narrow by aiService values present in current page items
  public applyAiServiceFromItems(items: InventoryResourceModel[]) {
    // Avoid over-pruning based on a single page; only use when absolutely necessary and when items span multiple services
    if (!items || items.length === 0) {
      return;
    }
    const present = new Set<string>();
    items.forEach((it) => {
      if (it.aiService) {
        present.add(it.aiService);
      }
    });
    if (present.size === 0 || present.size === 1) {
      return;
    }
    const current = this.aiServiceFilters.getOptions$().value;
    const selectedIds = new Set(this.aiServiceFilters.getSelectedFiltersIds());
    let filtered = current.filter((opt) => present.has(opt.value));
    // Keep selected options visible
    const selectedOptionsToKeep = current.filter((opt) => selectedIds.has(opt.id));
    const mergedById = new Map<string, (typeof current)[number]>();
    [...filtered, ...selectedOptionsToKeep].forEach((opt) => mergedById.set(opt.id, opt));
    filtered = Array.from(mergedById.values());
    if (selectedIds.size === 0) {
      this._aiServiceBaselineOptions = filtered.length > 0 ? filtered : this._aiServiceBaselineOptions;
    }
    const shouldUpdate = this.aiServiceFilters.updateOptions(filtered);
    this._updateAllOptions();
    if (shouldUpdate) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getDynamicFilters(): string[] {
    return [
      FilterTypesEnum.Environment,
      FilterTypesEnum.CreatedBy,
      FilterTypesEnum.OwnedBy,
      FilterTypesEnum.Department,
    ];
  }

  public getNewFilters(): string[] {
    return [FilterTypesEnum.AiService, FilterTypesEnum.RefreshMethod];
  }

  public getAllSelectedOptions(): ISelectOption[] {
    let selectedOptions: ISelectOption[] = [];

    if (this.identityIsExternal.getFilterValue() !== undefined) {
      selectedOptions.push(this._identityIsExternalOptions);
    }

    if (this.allBuilders.getFilterValue() !== undefined) {
      selectedOptions.push(this._allBuildersOptions);
    }

    if (this.connectionInUse.getFilterValue() !== undefined) {
      selectedOptions.push(this._connectionNotInUseOptions);
    }

    if (this.automationUsesAiModel.getFilterValue() !== undefined) {
      selectedOptions.push(this._automationUsesAiModelOptions);
    }

    if (this.automationUsesWebhook.getFilterValue() !== undefined) {
      selectedOptions.push(this._automationUsesWebhook);
    }

    if (this.automationUsesHttpStepsWithoutAuthentication.getFilterValue() !== undefined) {
      selectedOptions.push(this._automationUsesHttpStepsWithoutAuthentication);
    }

    if (this.automationIsBotTrigger.getFilterValue() !== undefined) {
      selectedOptions.push(this._automationIsBotTrigger);
    }

    if (this.isMcpHost.getFilterValue() !== undefined) {
      selectedOptions.push(this._isMcpHostOptions);
    }

    if (this.relatedResource.getFilterValue() !== undefined) {
      const resourceId = this.relatedResource.getFilterValue();
      selectedOptions.push({
        id: resourceId,
        value: resourceId,
        displayName: resourceId,
        filterType: FilterTypesEnum.RelatedResource,
        categoryName: 'Related Resource',
      } as ISelectOption);
    }

    if (this.environmentType.getFilterValue() !== undefined) {
      const environmentType = this.environmentType.getFilterValue();
      selectedOptions.push({
        id: environmentType,
        value: environmentType,
        displayName: environmentType,
        filterType: FilterTypesEnum.EnvironmentType,
        categoryName: 'Environment Type',
      } as ISelectOption);
    }

    // AI Service selected options are already appended above; avoid duplicates.

    if (this.botType.getFilterValue() !== undefined) {
      const botType = this.botType.getFilterValue();
      selectedOptions.push({
        id: botType,
        value: botType,
        displayName: botType,
        filterType: FilterTypesEnum.BotType,
        categoryName: 'Agent Type',
      } as ISelectOption);
    }

    if (this.orgWideAccess.getFilterValue() !== undefined) {
      selectedOptions.push(this._orgWideAccessOptions);
    }

    if (this.publicAccess.getFilterValue() !== undefined) {
      selectedOptions.push(this._publicAccessOptions);
    }

    if (this.hasWritePermission.getFilterValue() !== undefined) {
      selectedOptions.push(this._hasWritePermissionOptions);
    }

    selectedOptions = [
      ...selectedOptions,
      ...this.resourceCategoryFilters.getSelectedOptions(),
      ...this.typeFilters.getSelectedOptions(),
      ...this.resourceStatusFilters.getSelectedOptions(),
      ...this.platformFilters.getSelectedOptions(),
      ...this.businessCriticalityFilters.getSelectedOptions(),
      ...this.securityRiskFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.environmentFilters.getSelectedOptions(),
      ...this.createdByFilters.getSelectedOptions(),
      ...this.ownedByFilters.getSelectedOptions(),
      ...this.departmentFilters.getSelectedOptions(),
      ...this.creationTimeSinceDate.getSelectedOptions(),
      ...this.lastModifiedSinceDate.getSelectedOptions(),
      ...this.connectionConnectorType.getSelectedOptions(),
      ...this.refreshMethodFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      identityIsExternal: this.identityIsExternal.getFilterValue(),
      allBuilders: this.allBuilders.getFilterValue(),
      connectionInUse: this.connectionInUse.getFilterValue(),
      search: this.search.getFilterValue(),
      relatedResource: this.relatedResource.getFilterValue(),
      environmentType: this.environmentType.getFilterValue(),
      botType: this.botType.getFilterValue(),
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      lastModifiedSinceDate: this.lastModifiedSinceDate.getSelectedFiltersValues(),
      creationTimeSinceDate: this.creationTimeSinceDate.getSelectedFiltersValues(),
      connectionConnectorType: this.connectionConnectorType.getSelectedFiltersValues(),
      category: this.resourceCategoryFilters.getSelectedFiltersValues(),
      type: this.typeFilters.getSelectedFiltersValues(),
      resourceStatus: this.resourceStatusFilters.getSelectedFiltersValues(),
      platform: this.platformFilters.getSelectedFiltersValues(),
      businessCriticality: this.businessCriticalityFilters.getSelectedFiltersValues(),
      securityRisk: this.securityRiskFilters.getSelectedFiltersValues(),
      aiService: this.aiServiceFilters.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      environment: this.environmentFilters.getSelectedFiltersValues(),
      createdBy: this.createdByFilters.getSelectedFiltersValues(),
      ownedBy: this.ownedByFilters.getSelectedFiltersValues(),
      department: this.departmentFilters.getSelectedFiltersValues(),
      automationUsesAiModel: this.automationUsesAiModel.getFilterValue(),
      automationUsesWebhook: this.automationUsesWebhook.getFilterValue(),
      automationUsesHttpStepsWithoutAuthentication: this.automationUsesHttpStepsWithoutAuthentication.getFilterValue(),
      automationIsBotTrigger: this.automationIsBotTrigger.getFilterValue(),
      orgWideAccess: this.orgWideAccess.getFilterValue(),
      publicAccess: this.publicAccess.getFilterValue(),
      isMcpHost: this.isMcpHost.getFilterValue(),
      hasWritePermission: this.hasWritePermission.getFilterValue(),
      refreshMethod: this.refreshMethodFilters.getSelectedFiltersValues(),
    } as ISelectedFilters;
  }

  // * IMPORTANT - Options that are not shown in the filters do not need to be updated
  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;

    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.Search:
          this.search.update(value as string | undefined);
          needUpdate = true;
          break;
      }
    } else {
      if (value instanceof Array) {
        if (value.length == 0) {
          needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          value.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          this._updateAllOptions();

          // Update all filter types - use empty array for types not present in selection
          // This ensures that unchecked filters are properly removed
          needUpdate =
            this.platformFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Platform] || []) || needUpdate;
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
          needUpdate =
            this.resourceCategoryFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ResourceCategory] || []) ||
            needUpdate;
          needUpdate =
            this.typeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ResourceType] || []) || needUpdate;
          needUpdate =
            this.resourceStatusFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ResourceStatus] || []) ||
            needUpdate;
          needUpdate =
            this.businessCriticalityFilters.updateIfNeeded(
              optionsByCategory[FilterTypesEnum.BusinessCriticality] || []
            ) || needUpdate;
          needUpdate =
            this.securityRiskFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.SecurityRisk] || []) ||
            needUpdate;
          needUpdate =
            this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AiService] || []) || needUpdate;
          needUpdate =
            this.refreshMethodFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.RefreshMethod] || []) ||
            needUpdate;
          needUpdate =
            this.environmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Environment] || []) || needUpdate;
          needUpdate =
            this.createdByFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.CreatedBy] || []) || needUpdate;
          needUpdate =
            this.ownedByFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.OwnedBy] || []) || needUpdate;
          needUpdate =
            this.departmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Department] || []) || needUpdate;

          const categoryNewValues = optionsByCategory[FilterTypesEnum.ResourceCategory] || [];

          const identityInCategories =
            categoryNewValues.find((option) => option.value === ResourceCategory.identity) !== undefined;
          if (!identityInCategories && this.identityIsExternal.getFilterValue() !== undefined) {
            this.identityIsExternal.update(undefined);
            needUpdate = true;
          }
          if (!identityInCategories && this.allBuilders.getFilterValue() !== undefined) {
            this.allBuilders.update(undefined);
            needUpdate = true;
          }

          const connectionInCategories =
            categoryNewValues.find((option) => option.value === ResourceCategory.connection) !== undefined;
          if (
            !connectionInCategories &&
            (this.connectionConnectorType.getSelectedOptions().length > 0 ||
              this.connectionInUse.getFilterValue() !== undefined)
          ) {
            this.connectionInUse.update(undefined);
            this.connectionConnectorType.updateIfNeeded([]);
            needUpdate = true;
          }

          const automationInCategories =
            categoryNewValues.find((option) => option.value === ResourceCategory.automation) !== undefined;
          if (!automationInCategories && this.automationUsesAiModel.getFilterValue() !== undefined) {
            this.automationUsesAiModel.update(undefined);
            needUpdate = true;
          }

          if (!automationInCategories && this.automationUsesWebhook.getFilterValue() !== undefined) {
            this.automationUsesWebhook.update(undefined);
            needUpdate = true;
          }
          if (
            !automationInCategories &&
            this.automationUsesHttpStepsWithoutAuthentication.getFilterValue() !== undefined
          ) {
            this.automationUsesHttpStepsWithoutAuthentication.update(undefined);
            needUpdate = true;
          }

          if (!automationInCategories && this.automationIsBotTrigger.getFilterValue() !== undefined) {
            this.automationIsBotTrigger.update(undefined);
            needUpdate = true;
          }

          if (this.integrationFilters.getSelectedFiltersIds().length != 1 && this.relatedResource.getFilterValue()) {
            this.relatedResource.update(undefined);
            needUpdate = true;
          }

          const environmentInCategories =
            categoryNewValues.find((option) => option.value === ResourceCategory.environment) !== undefined;
          if (environmentInCategories && this.environmentType.getFilterValue()) {
            this.relatedResource.update(undefined);
            needUpdate = true;
          }

          const botInCategories =
            categoryNewValues.find((option) => option.value === ResourceCategory.bot) !== undefined;
          if (botInCategories && this.botType.getFilterValue()) {
            this.relatedResource.update(undefined);
            needUpdate = true;
          }

          if (!botInCategories && this.isMcpHost.getFilterValue() !== undefined) {
            this.isMcpHost.update(undefined);
            needUpdate = true;
          }

          if (this.orgWideAccess.getFilterValue() !== undefined) {
            this.orgWideAccess.update(undefined);
            needUpdate = true;
          }

          if (this.publicAccess.getFilterValue() !== undefined) {
            this.publicAccess.update(undefined);
            needUpdate = true;
          }
        }
      }
    }
    if (needUpdate) {
      this._handleSortedColumnOnFilter(filterToUpdate);
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: { q: filtersQuery },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};

    // Initialize dynamic filters
    this.getDynamicFilters().forEach((filter) => {
      optionsIdsByCategory[filter] = new Set();
    });

    // Process options to remove
    options.forEach((option) => {
      if (option.filterType) {
        if (option.filterType in optionsIdsByCategory) {
          optionsIdsByCategory[option.filterType].add(option.id);
        } else {
          optionsIdsByCategory[option.filterType] = new Set([option.id]);
        }
      }
    });

    if (optionsIdsByCategory[FilterTypesEnum.Platform]) {
      this.platformFilters.updateIfNeeded(
        this.platformFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Platform] &&
              !optionsIdsByCategory[FilterTypesEnum.Platform].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Integration] &&
              !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.ResourceCategory]) {
      this.resourceCategoryFilters.updateIfNeeded(
        this.resourceCategoryFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ResourceCategory].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.ResourceType]) {
      this.typeFilters.updateIfNeeded(
        this.typeFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.ResourceType] &&
              !optionsIdsByCategory[FilterTypesEnum.ResourceType].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.ResourceStatus]) {
      this.resourceStatusFilters.updateIfNeeded(
        this.resourceStatusFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.ResourceStatus] &&
              !optionsIdsByCategory[FilterTypesEnum.ResourceStatus].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.BusinessCriticality]) {
      this.businessCriticalityFilters.updateIfNeeded(
        this.businessCriticalityFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.BusinessCriticality] &&
              !optionsIdsByCategory[FilterTypesEnum.BusinessCriticality].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.SecurityRisk]) {
      this.securityRiskFilters.updateIfNeeded(
        this.securityRiskFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.SecurityRisk] &&
              !optionsIdsByCategory[FilterTypesEnum.SecurityRisk].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.AiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.AiService] &&
              !optionsIdsByCategory[FilterTypesEnum.AiService].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.RefreshMethod]) {
      this.refreshMethodFilters.updateIfNeeded(
        this.refreshMethodFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.RefreshMethod] &&
              !optionsIdsByCategory[FilterTypesEnum.RefreshMethod].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.LastModifiedSinceDate]) {
      this.lastModifiedSinceDate.updateIfNeeded(
        this.lastModifiedSinceDate
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.LastModifiedSinceDate] &&
              !optionsIdsByCategory[FilterTypesEnum.LastModifiedSinceDate].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.CreationTimeSinceDate]) {
      this.creationTimeSinceDate.updateIfNeeded(
        this.creationTimeSinceDate
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.CreationTimeSinceDate] &&
              !optionsIdsByCategory[FilterTypesEnum.CreationTimeSinceDate].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.ConnectionConnectorType]) {
      this.connectionConnectorType.updateIfNeeded(
        this.connectionConnectorType
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.ConnectionConnectorType] &&
              !optionsIdsByCategory[FilterTypesEnum.ConnectionConnectorType].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Environment]) {
      this.environmentFilters.updateIfNeeded(
        this.environmentFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Environment] &&
              !optionsIdsByCategory[FilterTypesEnum.Environment].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.CreatedBy]) {
      this.createdByFilters.updateIfNeeded(
        this.createdByFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.CreatedBy] &&
              !optionsIdsByCategory[FilterTypesEnum.CreatedBy].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.OwnedBy]) {
      this.ownedByFilters.updateIfNeeded(
        this.ownedByFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.OwnedBy] &&
              !optionsIdsByCategory[FilterTypesEnum.OwnedBy].has(selectedOption.id)
          )
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.Department]) {
      this.departmentFilters.updateIfNeeded(
        this.departmentFilters
          .getSelectedOptions()
          .filter(
            (selectedOption) =>
              optionsIdsByCategory[FilterTypesEnum.Department] &&
              !optionsIdsByCategory[FilterTypesEnum.Department].has(selectedOption.id)
          )
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.RelatedResource]) {
      this.relatedResource.update(undefined);
    }

    if (optionsIdsByCategory[FilterTypesEnum.EnvironmentType]) {
      this.environmentType.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.BotType]) {
      this.botType.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.IdentityIsExternal]) {
      this.identityIsExternal.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.allBuilders]) {
      this.allBuilders.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.ConnectionInUse]) {
      this.connectionInUse.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.AutomationUsesAiModel]) {
      this.automationUsesAiModel.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.AutomationUsesWebhook]) {
      this.automationUsesWebhook.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.AutomationIsBotTrigger]) {
      this.automationIsBotTrigger.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.AutomationUsesHttpStepsWithoutAuthentication]) {
      this.automationUsesHttpStepsWithoutAuthentication.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.OrgWideAccess]) {
      this.orgWideAccess.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.PublicAccess]) {
      this.publicAccess.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.IsMcpHost]) {
      this.isMcpHost.update(undefined);
    }
    if (optionsIdsByCategory[FilterTypesEnum.HasWritePermission]) {
      this.hasWritePermission.update(undefined);
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { q: filtersQuery },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.platformFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.resourceCategoryFilters.updateIfNeeded([]) ||
      this.typeFilters.updateIfNeeded([]) ||
      this.resourceStatusFilters.updateIfNeeded([]) ||
      this.businessCriticalityFilters.updateIfNeeded([]) ||
      this.securityRiskFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.refreshMethodFilters.updateIfNeeded([]) ||
      this.environmentFilters.updateIfNeeded([]) ||
      this.createdByFilters.updateIfNeeded([]) ||
      this.ownedByFilters.updateIfNeeded([]) ||
      this.departmentFilters.updateIfNeeded([])
    );
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.Platform:
        return this.platformFilters.getOptions$();

      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();

      case FilterTypesEnum.ResourceCategory:
        return this.resourceCategoryFilters.getOptions$();

      case FilterTypesEnum.ResourceType:
        return this.typeFilters.getOptions$();

      case FilterTypesEnum.ResourceStatus:
        return this.resourceStatusFilters.getOptions$();

      case FilterTypesEnum.BusinessCriticality:
        return this.businessCriticalityFilters.getOptions$();

      case FilterTypesEnum.SecurityRisk:
        return this.securityRiskFilters.getOptions$();

      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getOptions$();

      case FilterTypesEnum.Environment:
        return this.environmentFilters.getOptions$();

      case FilterTypesEnum.CreatedBy:
        return this.createdByFilters.getOptions$();

      case FilterTypesEnum.OwnedBy:
        return this.ownedByFilters.getOptions$();

      case FilterTypesEnum.Department:
        return this.departmentFilters.getOptions$();

      case FilterTypesEnum.RefreshMethod:
        return this.refreshMethodFilters.getOptions$();

      default:
        throw new Error(`InventoryFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.Platform:
        return this.platformFilters.getSelectedFiltersIds();

      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();

      case FilterTypesEnum.Environment:
        return this.environmentFilters.getSelectedFiltersIds();

      case FilterTypesEnum.ResourceCategory:
        return this.resourceCategoryFilters.getSelectedFiltersIds();

      case FilterTypesEnum.ResourceType:
        return this.typeFilters.getSelectedFiltersIds();

      case FilterTypesEnum.ResourceStatus:
        return this.resourceStatusFilters.getSelectedFiltersIds();

      case FilterTypesEnum.BusinessCriticality:
        return this.businessCriticalityFilters.getSelectedFiltersIds();

      case FilterTypesEnum.SecurityRisk:
        return this.securityRiskFilters.getSelectedFiltersIds();

      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getSelectedFiltersIds();

      case FilterTypesEnum.RefreshMethod:
        return this.refreshMethodFilters.getSelectedFiltersIds();

      default:
        throw new Error(`InventoryFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values;

    if ('fromDate' in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
      const fromDateMoment =
        keyValueFilters['fromDate'] && moment(keyValueFilters['fromDate'][0], 'x').isValid()
          ? moment(keyValueFilters['fromDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const fromDate = fromDateMoment && !fromDateMoment.isAfter(moment()) ? fromDateMoment.toDate() : undefined;

      this.fromDate.update(fromDate);
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
      const toDateMoment =
        keyValueFilters['toDate'] && moment(keyValueFilters['toDate'][0], 'x').isValid()
          ? moment(keyValueFilters['toDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const toDate = toDateMoment && !toDateMoment.isAfter(moment()) ? toDateMoment.toDate() : undefined;

      this.toDate.update(toDate);
      needUpdate = true;
    }

    if ('search' in keyValueFilters || this.search.getFilterValue() !== undefined) {
      this.search.update(keyValueFilters['search'] ? keyValueFilters['search'][0] : undefined);
      needUpdate = true;
    }

    const integrationNewValues = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(integrationNewValues) || needUpdate;

    if ('relatedResource' in keyValueFilters || this.relatedResource.getFilterValue() !== undefined) {
      this.relatedResource.update(
        keyValueFilters['relatedResource'] && integrationNewValues.length === 1
          ? keyValueFilters['relatedResource'][0]
          : undefined
      );
      needUpdate = true;
    }

    const categoryNewValues = 'category' in keyValueFilters ? keyValueFilters['category'] : [];
    needUpdate = this.resourceCategoryFilters.updateByValuesIfNeeded(categoryNewValues) || needUpdate;

    const onlyEnvironmentSelected =
      categoryNewValues.length == 1 && categoryNewValues[0] == ResourceCategory.environment;
    if ('environmentType' in keyValueFilters || this.environmentType.getFilterValue() !== undefined) {
      this.environmentType.update(
        onlyEnvironmentSelected && keyValueFilters['environmentType']
          ? keyValueFilters['environmentType'][0]
          : undefined
      );
      needUpdate = true;
    }

    const onlyBotSelected = categoryNewValues.length == 1 && categoryNewValues[0] == ResourceCategory.bot;
    if ('botType' in keyValueFilters || this.botType.getFilterValue() !== undefined) {
      this.botType.update(onlyBotSelected && keyValueFilters['botType'] ? keyValueFilters['botType'][0] : undefined);
      needUpdate = true;
    }

    if ('identityIsExternal' in keyValueFilters || this.identityIsExternal.getFilterValue() !== undefined) {
      const onlyIdentitySelected = categoryNewValues.length == 1 && categoryNewValues[0] == ResourceCategory.identity;
      this.identityIsExternal.update(
        onlyIdentitySelected &&
          keyValueFilters['identityIsExternal'] &&
          keyValueFilters['identityIsExternal'][0] == 'true'
          ? true
          : undefined
      );
      needUpdate = true;
    }

    if ('allBuilders' in keyValueFilters || this.allBuilders.getFilterValue() !== undefined) {
      const onlyIdentitySelected = categoryNewValues.length == 1 && categoryNewValues[0] == ResourceCategory.identity;
      this.allBuilders.update(
        onlyIdentitySelected && keyValueFilters['allBuilders'] && keyValueFilters['allBuilders'][0] == 'true'
          ? true
          : undefined
      );
      needUpdate = true;
    }

    const onlyConnectionsSelected =
      categoryNewValues.length == 1 && categoryNewValues[0] == ResourceCategory.connection;
    if ('connectionInUse' in keyValueFilters || this.connectionInUse.getFilterValue() !== undefined) {
      this.connectionInUse.update(
        onlyConnectionsSelected &&
          keyValueFilters['connectionInUse'] &&
          keyValueFilters['connectionInUse'][0] == 'false'
          ? false
          : undefined
      );
      needUpdate = true;
    }
    values =
      onlyConnectionsSelected && 'connectionConnectorType' in keyValueFilters
        ? keyValueFilters['connectionConnectorType']
        : [];
    needUpdate = this.connectionConnectorType.updateByValuesIfNeeded(values) || needUpdate;

    const onlyAutomationSelected = categoryNewValues.length == 1 && categoryNewValues[0] == ResourceCategory.automation;
    if ('automationUsesAiModel' in keyValueFilters || this.automationUsesAiModel.getFilterValue() !== undefined) {
      this.automationUsesAiModel.update(
        onlyAutomationSelected &&
          keyValueFilters['automationUsesAiModel'] &&
          keyValueFilters['automationUsesAiModel'][0] == 'true'
          ? true
          : undefined
      );
      needUpdate = true;
    }
    if ('automationUsesWebhook' in keyValueFilters || this.automationUsesWebhook.getFilterValue() !== undefined) {
      this.automationUsesWebhook.update(
        onlyAutomationSelected &&
          keyValueFilters['automationUsesWebhook'] &&
          keyValueFilters['automationUsesWebhook'][0] == 'true'
          ? true
          : undefined
      );
      needUpdate = true;
    }
    if (
      'automationUsesHttpStepsWithoutAuthentication' in keyValueFilters ||
      this.automationUsesHttpStepsWithoutAuthentication.getFilterValue() !== undefined
    ) {
      this.automationUsesHttpStepsWithoutAuthentication.update(
        onlyAutomationSelected &&
          keyValueFilters['automationUsesHttpStepsWithoutAuthentication'] &&
          keyValueFilters['automationUsesHttpStepsWithoutAuthentication'][0] == 'true'
          ? true
          : undefined
      );
      needUpdate = true;
    }

    if ('automationIsBotTrigger' in keyValueFilters || this.automationIsBotTrigger.getFilterValue() !== undefined) {
      this.automationIsBotTrigger.update(
        onlyAutomationSelected &&
          keyValueFilters['automationIsBotTrigger'] &&
          keyValueFilters['automationIsBotTrigger'][0] == 'true'
          ? true
          : undefined
      );
      needUpdate = true;
    }

    if ('isMcpHost' in keyValueFilters || this.isMcpHost.getFilterValue() !== undefined) {
      this.isMcpHost.update(
        keyValueFilters['isMcpHost'] && keyValueFilters['isMcpHost'][0] == 'true' ? true : undefined
      );
      needUpdate = true;
    }

    if ('orgWideAccess' in keyValueFilters || this.orgWideAccess.getFilterValue() !== undefined) {
      this.orgWideAccess.update(
        keyValueFilters['orgWideAccess'] && keyValueFilters['orgWideAccess'][0] == 'true' ? true : undefined
      );
      needUpdate = true;
    }

    if ('publicAccess' in keyValueFilters || this.publicAccess.getFilterValue() !== undefined) {
      this.publicAccess.update(
        keyValueFilters['publicAccess'] && keyValueFilters['publicAccess'][0] == 'true' ? true : undefined
      );
      needUpdate = true;
    }

    if ('hasWritePermission' in keyValueFilters || this.hasWritePermission.getFilterValue() !== undefined) {
      this.hasWritePermission.update(
        keyValueFilters['hasWritePermission'] && keyValueFilters['hasWritePermission'][0] == 'true' ? true : undefined
      );
      needUpdate = true;
    }

    values = 'lastModifiedSinceDate' in keyValueFilters ? keyValueFilters['lastModifiedSinceDate'] : [];
    needUpdate = this.lastModifiedSinceDate.updateByValuesIfNeeded(values) || needUpdate;

    values = 'creationTimeSinceDate' in keyValueFilters ? keyValueFilters['creationTimeSinceDate'] : [];
    needUpdate = this.creationTimeSinceDate.updateByValuesIfNeeded(values) || needUpdate;

    values = 'platform' in keyValueFilters ? keyValueFilters['platform'] : [];
    needUpdate = this.platformFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'businessCriticality' in keyValueFilters ? keyValueFilters['businessCriticality'] : [];
    needUpdate = this.businessCriticalityFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'securityRisk' in keyValueFilters ? keyValueFilters['securityRisk'] : [];
    needUpdate = this.securityRiskFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'aiService' in keyValueFilters ? keyValueFilters['aiService'] : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'refreshMethod' in keyValueFilters ? keyValueFilters['refreshMethod'] : [];
    needUpdate = this.refreshMethodFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'resourceStatus' in keyValueFilters ? keyValueFilters['resourceStatus'] : [];
    needUpdate = this.resourceStatusFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'type' in keyValueFilters ? keyValueFilters['type'] : [];
    needUpdate = this.typeFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'environment' in keyValueFilters ? keyValueFilters['environment'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.environmentFilters.getOptions$().value, values)) {
      this.environmentFilters = new DynamicMultiSelectFilters([]);
      this._fetchAndUpdateFilterOptions(values, FilterTypesEnum.Environment, 'environment', this.environmentFilters);
    }
    needUpdate = this.environmentFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'createdBy' in keyValueFilters ? keyValueFilters['createdBy'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.createdByFilters.getOptions$().value, values)) {
      this.createdByFilters = new DynamicMultiSelectFilters([]);
      this._fetchAndUpdateFilterOptions(values, FilterTypesEnum.CreatedBy, 'user', this.createdByFilters);
    }
    needUpdate = this.createdByFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'ownedBy' in keyValueFilters ? keyValueFilters['ownedBy'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.ownedByFilters.getOptions$().value, values)) {
      this.ownedByFilters = new DynamicMultiSelectFilters([]);
      this._fetchAndUpdateFilterOptions(values, FilterTypesEnum.OwnedBy, 'user', this.ownedByFilters);
    }
    needUpdate = this.ownedByFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'department' in keyValueFilters ? keyValueFilters['department'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.departmentFilters.getOptions$().value, values)) {
      this.departmentFilters = new DynamicMultiSelectFilters([]);
      this._departmentOnUpdate(values);
    }
    needUpdate = this.departmentFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public restoreSortColumnToDefaultIfNeeded() {
    if (!this.search.getFilterValue()) {
      this.sortedColumn$.next(this._defaultSortColumn);
    }
  }

  private _initializeFilters() {
    const updatedResourceCategorySelectOptions = this._removeAiExtensionsFromCategoryFilters(
      // TODO: when devices FF removed delete getFilteredResourceCategorySelectOptions and uncomment getResourceCategorySelectOptions
      // #### Devices FF ####
      getFilteredResourceCategorySelectOptions(this._beautifyDisplayStringPipe, this.featureFlagsService)
      // getResourceCategorySelectOptions(this._beautifyDisplayStringPipe)
    );
    const filteredResourceTypeSelectOptions = removeUnusedFilterByFeatureFlag(
      ResourceTypeSelectOptions,
      this.featureFlagsService
    );

    this.identityIsExternal = new BooleanFilter();
    this.allBuilders = new BooleanFilter();
    this.connectionInUse = new BooleanFilter();
    this.automationUsesAiModel = new BooleanFilter();
    this.automationUsesWebhook = new BooleanFilter();
    this.automationUsesHttpStepsWithoutAuthentication = new BooleanFilter();
    this.automationIsBotTrigger = new BooleanFilter();
    this.isMcpHost = new BooleanFilter();
    this.creationTimeSinceDate = new MultiSelectFilters(CreationTimeSinceOptions);
    this.lastModifiedSinceDate = new MultiSelectFilters(LastModifiedSinceOptions);
    this.connectionConnectorType = new MultiSelectFilters(ConnectionConnectorTypeSelectOptions);
    this.search = new TextFilter();
    this.environmentType = new TextFilter();
    this.botType = new TextFilter();
    this.relatedResource = new TextFilter();
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.resourceCategoryFilters = new MultiSelectFilters(updatedResourceCategorySelectOptions);
    this.typeFilters = new MultiSelectFilters(filteredResourceTypeSelectOptions);
    this.platformFilters = new MultiSelectFilters(PlatformSelectOptions);
    this.businessCriticalityFilters = new MultiSelectFilters(BusinessCriticalitiesSelectOptions);
    this.securityRiskFilters = new MultiSelectFilters(SecurityRiskSelectOptions);
    const isRsaDemo = this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
    const aiServiceOpts = isRsaDemo ? AiServiceSelectOptionsWithRsaDemo : AiServiceSelectOptions;
    this.aiServiceFilters = new MultiSelectFilters(aiServiceOpts);
    this.integrationFilters = new MultiSelectFilters([]);
    this.refreshMethodFilters = new MultiSelectFilters(RefreshMethodSelectOptions);
    this.resourceStatusFilters = new MultiSelectFilters(ResourceStatusSelectOptions);
    this.environmentFilters = new DynamicMultiSelectFilters([]);
    this.createdByFilters = new DynamicMultiSelectFilters([]);
    this.ownedByFilters = new DynamicMultiSelectFilters([]);
    this.departmentFilters = new DynamicMultiSelectFilters([]);
    this.orgWideAccess = new BooleanFilter();
    this.publicAccess = new BooleanFilter();
    this.isMcpHost = new BooleanFilter();
    this.hasWritePermission = new BooleanFilter();
    this._updateAllOptions();
  }

  private _removeAiExtensionsFromCategoryFilters(resourceCategorySelectOptions: ISelectOption[]): ISelectOption[] {
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.AiExtensions)) {
      return resourceCategorySelectOptions;
    }

    return resourceCategorySelectOptions.filter((option) => option.value !== ResourceCategory.aiExtension);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for inventory viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.INVENTORY_VIEWER
      );

      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = filteredIntegrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);
        return fromIntegrationToSelectOption(integration);
      });

      let shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));

      const platformOptions = this.platformFilters
        .getOptions$()
        .value.filter((option) => integrationsPlatformSet.has(option.value));

      const supportedTypesSet = new Set<string>();
      platformOptions.forEach((platformOption) => {
        const platformResourceTypes = ResourceTypesPerPlatform[platformOption.id] ?? [];
        platformResourceTypes.forEach((resourceType) => {
          supportedTypesSet.add(resourceType);
        });
      });

      const deduplicatedTypesSet = deduplicateResourceTypesForUI(supportedTypesSet);

      const resourceTypesOptions = this.typeFilters
        .getOptions$()
        .value.filter((option) => deduplicatedTypesSet.has(option.value));

      const didUpdatePlatforms = this.platformFilters.updateOptions(platformOptions);
      const didUpdateTypes = this.typeFilters.updateOptions(resourceTypesOptions);
      shouldUpdateSelectedOptions = shouldUpdateSelectedOptions || didUpdatePlatforms || didUpdateTypes;

      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  private _fetchAndUpdateFilterOptions(
    ids: string[],
    filterType: FilterTypesEnum,
    filterName: string,
    filters: DynamicMultiSelectFilters
  ) {
    this._inventoryHttpService
      .getDisplayNameById(filterName, ids)
      .subscribe((autocompleteResults: AutocompleteSearchResultsModel[]) => {
        const options: ISelectOption[] = [];
        autocompleteResults.forEach((user) => {
          options.push({
            id: user.id,
            value: user.id,
            displayName: user.displayName,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        });

        const shouldUpdateSelectedOptions = filters.updateOptions(Object.values(options));

        this._updateAllOptions();
        if (shouldUpdateSelectedOptions) {
          this.onUpdate$.next(this.getSelectedFilters());
        }
      });
  }

  private _departmentOnUpdate(encodedDepartmentNames: string[]) {
    const selectedDepartments = encodedDepartmentsToSelection(encodedDepartmentNames);
    const shouldUpdateSelectedOptions = this.departmentFilters.updateOptions(Object.values(selectedDepartments));
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) this.onUpdate$.next(this.getSelectedFilters());
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.resourceCategoryFilters.getOptions$().value,
      ...this.typeFilters.getOptions$().value,
      ...this.resourceStatusFilters.getOptions$().value,
      ...this.platformFilters.getOptions$().value,
      ...this.businessCriticalityFilters.getOptions$().value,
      ...this.securityRiskFilters.getOptions$().value,
      ...(this.aiServiceFilters ? this.aiServiceFilters.getOptions$().value : []),
      ...(this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmNrtEnabled)
        ? this.refreshMethodFilters.getOptions$().value
        : []),
      ...this.integrationFilters.getOptions$().value,
      ...this.environmentFilters.getOptions$().value,
      ...this.createdByFilters.getOptions$().value,
      ...this.ownedByFilters.getOptions$().value,
      ...this.departmentFilters.getOptions$().value,
    ]);
  }

  private _initDefaultSortColumn() {
    const securityRiskColumn = InventoryTableColumns.find((column) => column.propertyKey === 'securityRisk');
    if (securityRiskColumn) {
      this._defaultSortColumn = {
        column: securityRiskColumn,
        isDesc: true,
      };
    }
  }

  private _handleSortedColumnOnFilter(filterToUpdate: FilterTypesEnum | undefined) {
    // When applying search we remove any sorted column in order to display results according to their relevancy to the search term
    if (filterToUpdate === FilterTypesEnum.Search && this.search.getFilterValue()) {
      this.sortedColumn$.next(undefined);
      return;
    }

    // Otherwise, for any other filter, if no column is already sorted restore to default sort column if needed
    if (this.sortedColumn$.value === undefined) {
      this.restoreSortColumnToDefaultIfNeeded();
    }
  }
}
